import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Copy, Edit3, Send, Archive, Lock, Save, Trash2, RotateCcw } from 'lucide-react';

// Construtor de versões do formulário da Avaliação 360. Espelha o do BSV, com três
// diferenças: a ÁREA tem CÓDIGO semântico (QSL/GP/…) e a flag PERMITE_NA_AREA
// (segmento que a loja pode não ter); a PERGUNTA tem SUBSEÇÃO (rótulo visual) e um
// CÓDIGO OPACO que é sua identidade — preservado ao editar, gerado pelo servidor na
// pergunta nova. A 360 não tem peso (cada pergunta conta 1).
const API_BASE = '/api/operacional/aval360/versoes';

const statusStyle = (s) => {
  if (s === 'Publicado') return { bg: '#d1fae5', color: '#065f46' };
  if (s === 'Rascunho')  return { bg: '#fef3c7', color: '#92400e' };
  return { bg: '#e2e8f0', color: '#475569' }; // Arquivado
};

const Aval360Versoes = ({ onVoltar }) => {
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(null); // ID em edição
  const [mostrarNova, setMostrarNova] = useState(false);

  const token = () => localStorage.getItem('authToken');
  const auth = () => ({ Authorization: `Bearer ${token()}` });

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(API_BASE, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar versões.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const novaVersao = async (nome) => {
    if (!nome?.trim()) return;
    const r = await fetch(API_BASE, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao criar.'); return; }
    setMostrarNova(false);
    await carregar();
    setEditando(data.id);
  };

  const duplicar = async (id) => {
    const nome = window.prompt('Nome da nova versão (cópia):');
    if (!nome?.trim()) return;
    const r = await fetch(`${API_BASE}/${id}/duplicar`, {
      method: 'POST',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ nome_versao: nome }),
    });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao duplicar.'); return; }
    await carregar();
    setEditando(data.id);
  };

  const publicar = async (id) => {
    if (!window.confirm('Publicar esta versão? Após o primeiro uso ela ficará imutável.')) return;
    const r = await fetch(`${API_BASE}/${id}/publicar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao publicar.'); return; }
    carregar();
  };

  const arquivar = async (id) => {
    if (!window.confirm('Arquivar esta versão? Ela some das listas mas as avaliações já feitas continuam preservadas.')) return;
    const r = await fetch(`${API_BASE}/${id}/arquivar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao arquivar.'); return; }
    carregar();
  };

  const republicar = async (id) => {
    if (!window.confirm('Republicar esta versão? Ela volta a aparecer na lista de formulários para preenchimento.')) return;
    const r = await fetch(`${API_BASE}/${id}/republicar`, { method: 'POST', headers: auth() });
    const data = await r.json();
    if (!r.ok) { setErro(data?.message || 'Erro ao republicar.'); return; }
    carregar();
  };

  if (editando) {
    return <VersaoEditor id={editando} onSair={() => { setEditando(null); carregar(); }} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={btnVoltar}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Gerenciar Versões da Avaliação 360</h3>
      </div>

      <button
        onClick={() => setMostrarNova(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 16px', background: '#475569', color: '#fff',
          border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16,
        }}
      >
        <Plus size={16} /> Nova versão (em branco)
      </button>

      {mostrarNova && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <input
            type="text" autoFocus placeholder="Nome da nova versão"
            onKeyDown={(e) => { if (e.key === 'Enter') novaVersao(e.target.value); if (e.key === 'Escape') setMostrarNova(false); }}
            style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            Pressione Enter para criar — a versão começa como Rascunho. Para partir da 360 oficial, use <strong>Duplicar</strong> na v1.
          </div>
        </div>
      )}

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {carregando && <p style={{ color: '#64748b' }}>Carregando…</p>}

      <div style={{ display: 'grid', gap: 10 }}>
        {versoes.map((v) => {
          const st = statusStyle(v.STATUS);
          const imutavel = v.STATUS === 'Publicado' && v.USOS > 0;
          return (
            <div key={v.ID} style={{
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                    background: st.bg, color: st.color,
                  }}>{v.STATUS}</span>
                  {imutavel && (
                    <span title="Versão imutável: já tem avaliações" style={{ color: '#64748b' }}>
                      <Lock size={14} />
                    </span>
                  )}
                </div>
                {v.DESCRICAO && <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{v.DESCRICAO}</div>}
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  Total de perguntas: {v.TOTAL_PERGUNTAS} · Usos: {v.USOS}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditando(v.ID)} title={imutavel ? 'Visualizar (imutável)' : 'Editar'}
                  style={btn('#f1f5f9', '#475569')}>
                  <Edit3 size={14} /> {imutavel ? 'Ver' : 'Editar'}
                </button>
                <button onClick={() => duplicar(v.ID)} title="Duplicar" style={btn('#eff6ff', '#1d4ed8')}>
                  <Copy size={14} /> Duplicar
                </button>
                {v.STATUS === 'Rascunho' && (
                  <button onClick={() => publicar(v.ID)} title="Publicar" style={btn('#d1fae5', '#065f46')}>
                    <Send size={14} /> Publicar
                  </button>
                )}
                {v.STATUS === 'Publicado' && (
                  <button onClick={() => arquivar(v.ID)} title="Arquivar" style={btn('#f1f5f9', '#475569')}>
                    <Archive size={14} /> Arquivar
                  </button>
                )}
                {v.STATUS === 'Arquivado' && (
                  <button onClick={() => republicar(v.ID)} title="Republicar" style={btn('#d1fae5', '#065f46')}>
                    <RotateCcw size={14} /> Republicar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const btn = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', background: bg, color, border: 'none',
  borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer',
});
const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

// ---------------------------------------------------------------------
// Editor da versão (mesmo arquivo p/ reduzir fragmentação)
// ---------------------------------------------------------------------
const VersaoEditor = ({ id, onSair }) => {
  const [dados, setDados] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const token = () => localStorage.getItem('authToken');
  const auth = () => ({ Authorization: `Bearer ${token()}` });

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, { headers: auth() });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao carregar.');
      setDados({
        nome_versao: data.versao.NOME_VERSAO,
        descricao: data.versao.DESCRICAO || '',
        status: data.versao.STATUS,
        podeEditar: data.podeEditar,
        usos: data.usos,
        areas: (data.areas || []).map((a) => ({
          id: a.ID, codigo: a.CODIGO || '', nome: a.NOME, ordem: a.ORDEM,
          permite_na_area: !!a.PERMITE_NA_AREA,
          perguntas: (a.perguntas || []).map((p) => ({
            id: p.ID, codigo: p.CODIGO || '', texto: p.TEXTO, subsecao: p.SUBSECAO || '', ordem: p.ORDEM,
          })),
        })),
      });
    } catch (e) {
      setErro(e.message);
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const ro = !dados.podeEditar;

  const atualizarArea = (i, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[i] = { ...novas[i], [campo]: valor };
      return { ...d, areas: novas };
    });
  };
  const adicionarArea = () => {
    setDados((d) => ({ ...d, areas: [...d.areas, { codigo: '', nome: 'Nova Área', ordem: d.areas.length + 1, permite_na_area: false, perguntas: [] }] }));
  };
  const removerArea = (i) => {
    setDados((d) => ({ ...d, areas: d.areas.filter((_, idx) => idx !== i) }));
  };
  const adicionarPergunta = (i) => {
    setDados((d) => {
      const novas = [...d.areas];
      // codigo vazio → o servidor gera um código aleatório único ao salvar.
      novas[i] = {
        ...novas[i],
        perguntas: [...novas[i].perguntas, { codigo: '', texto: '', subsecao: '', ordem: novas[i].perguntas.length + 1 }],
      };
      return { ...d, areas: novas };
    });
  };
  const atualizarPergunta = (ai, pi, campo, valor) => {
    setDados((d) => {
      const novas = [...d.areas];
      const ps = [...novas[ai].perguntas];
      ps[pi] = { ...ps[pi], [campo]: valor };
      novas[ai] = { ...novas[ai], perguntas: ps };
      return { ...d, areas: novas };
    });
  };
  const removerPergunta = (ai, pi) => {
    setDados((d) => {
      const novas = [...d.areas];
      novas[ai] = { ...novas[ai], perguntas: novas[ai].perguntas.filter((_, i) => i !== pi) };
      return { ...d, areas: novas };
    });
  };

  const salvar = async () => {
    setSalvando(true); setErro(''); setSucesso('');
    try {
      const r = await fetch(`${API_BASE}/${id}`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome_versao: dados.nome_versao,
          descricao: dados.descricao,
          areas: dados.areas.map((a, ai) => ({
            codigo: a.codigo, nome: a.nome, ordem: ai + 1, permite_na_area: a.permite_na_area,
            // Preserva o código da pergunta (identidade); vazio → o servidor gera.
            // Sem peso — a 360 é contagem pura.
            perguntas: a.perguntas.map((p, pi) => ({
              codigo: p.codigo || '', texto: p.texto, subsecao: p.subsecao || '', ordem: pi + 1,
            })),
          })),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Erro ao salvar.');
      setSucesso('Salvo.');
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const totalItens = dados.areas.reduce((acc, a) => acc + a.perguntas.length, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onSair} style={btnVoltar}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Editor: {dados.nome_versao}
        </h3>
      </div>

      {ro && (
        <div style={{
          padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 8, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Lock size={16} /> Versão imutável: já tem {dados.usos} avaliação(ões) gravada(s). Para modificar, use "Duplicar".
        </div>
      )}
      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}
      {sucesso && <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12 }}>{sucesso}</div>}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Nome da versão</label>
            <input type="text" disabled={ro} value={dados.nome_versao}
              onChange={(e) => setDados({ ...dados, nome_versao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Descrição</label>
            <input type="text" disabled={ro} value={dados.descricao}
              onChange={(e) => setDados({ ...dados, descricao: e.target.value })}
              style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>
          Status: <strong>{dados.status}</strong> · Total de perguntas: <strong>{totalItens}</strong> · Áreas: <strong>{dados.areas.length}</strong>
        </div>
      </div>

      {dados.areas.map((a, ai) => (
        <div key={ai} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <input type="text" disabled={ro} value={a.codigo} placeholder="Cód."
              onChange={(e) => atualizarArea(ai, 'codigo', e.target.value.toUpperCase())}
              title="Código da área (QSL, GP, ST, QSC, DEL, DRV) — semântico, amarra o segmento"
              style={{ width: 72, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 800, textAlign: 'center' }} />
            <input type="text" disabled={ro} value={a.nome}
              onChange={(e) => atualizarArea(ai, 'nome', e.target.value)}
              style={{ flex: 1, minWidth: 200, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontWeight: 700 }} />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', fontWeight: 700 }}
              title="Permite marcar a área INTEIRA como N/A (loja sem esse segmento — Quiosque/Delivery/Drive)">
              <input type="checkbox" disabled={ro} checked={a.permite_na_area}
                onChange={(e) => atualizarArea(ai, 'permite_na_area', e.target.checked)} />
              N/A por área
            </label>
            {!ro && (
              <button onClick={() => removerArea(ai)} style={btn('#fee2e2', '#991b1b')} title="Remover área">
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {a.perguntas.map((p, pi) => (
              <div key={pi} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Rótulo SEQUENCIAL (estilo PDF, QSL-01) — derivado de área+posição. O
                    código OPACO de identidade (usado para comparar versões) fica no
                    tooltip, não na tela. */}
                <span title={`Identidade (comparação entre versões): ${p.codigo || '(nova — gerada ao salvar)'}`} style={{
                  fontSize: 10, fontWeight: 800, color: '#64748b', background: '#f1f5f9',
                  border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 6px', minWidth: 70, textAlign: 'center',
                }}>{(a.codigo || '??')}-{String(pi + 1).padStart(2, '0')}</span>
                <input type="text" disabled={ro} value={p.subsecao} placeholder="Subseção (opcional)"
                  onChange={(e) => atualizarPergunta(ai, pi, 'subsecao', e.target.value)}
                  title="Rótulo visual dentro da área (ex.: Qualidade, Serviço). Opcional."
                  style={{ width: 150, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }} />
                <input type="text" disabled={ro} value={p.texto} placeholder="Texto da pergunta"
                  onChange={(e) => atualizarPergunta(ai, pi, 'texto', e.target.value)}
                  style={{ flex: 1, minWidth: 220, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                {!ro && (
                  <button onClick={() => removerPergunta(ai, pi)} style={btn('#fee2e2', '#991b1b')} title="Remover">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
            {!ro && (
              <button onClick={() => adicionarPergunta(ai)} style={btn('#eff6ff', '#1d4ed8')}>
                <Plus size={12} /> Pergunta
              </button>
            )}
          </div>
        </div>
      ))}

      {!ro && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={adicionarArea} style={{
            padding: '10px 16px', background: '#f1f5f9', color: '#475569',
            border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <Plus size={14} /> Área
          </button>
          <button onClick={salvar} disabled={salvando} style={{
            padding: '10px 20px', background: salvando ? '#94a3b8' : '#475569', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 800, cursor: salvando ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={14} /> {salvando ? 'Salvando…' : 'Salvar estrutura'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Aval360Versoes;
